<script>
  import { createEventDispatcher } from 'svelte';
  import PropertyEditor from '../common/PropertyEditor.svelte';
  
  const dispatch = createEventDispatcher();
  
  export let link;
  export let linkIndex;
  export let selectedLinkIndex;
  export let updateWorkingQuestion;
  export let question;
  export let questionIndex;

  let linkTypes, customProperties, sourceTargetOptions;
  
  function selectLink() {
    // Instead of binding, dispatch an event
    dispatch('selectLink', linkIndex);
  }
  
  function getLinkTypeValues() {
    if (!link.type) return ['link-type'];
    return Array.isArray(link.type) ? link.type : [link.type];
  }
  
  function updateLinkTypeValue(typeIndex, value) {
    const dataset = [...question.dataset];
    const linksHandlerIndex = dataset.findIndex(handler => handler[0] === 'links');
    
    if (linksHandlerIndex !== -1) {
      const handler = [...dataset[linksHandlerIndex]];
      const links = [...handler[1]];
      const linkCopy = { ...links[linkIndex] };
      
      const types = Array.isArray(linkCopy.type) ? [...linkCopy.type] : [linkCopy.type];
      types[typeIndex] = value;
      linkCopy.type = types.length === 1 ? types[0] : types;
      
      links[linkIndex] = linkCopy;
      handler[1] = links;
      dataset[linksHandlerIndex] = handler;
      updateWorkingQuestion('dataset', dataset);
    }
  }
  
  function addLinkTypeValue() {
    const dataset = [...question.dataset];
    const linksHandlerIndex = dataset.findIndex(handler => handler[0] === 'links');
    
    if (linksHandlerIndex !== -1) {
      const handler = [...dataset[linksHandlerIndex]];
      const links = [...handler[1]];
      const linkCopy = { ...links[linkIndex] };
      
      const types = Array.isArray(linkCopy.type) ? [...linkCopy.type] : [linkCopy.type];
      types.push('new-type');
      linkCopy.type = types;
      
      links[linkIndex] = linkCopy;
      handler[1] = links;
      dataset[linksHandlerIndex] = handler;
      updateWorkingQuestion('dataset', dataset);
    }
  }
  
  function removeLinkTypeValue(typeIndex) {
    const dataset = [...question.dataset];
    const linksHandlerIndex = dataset.findIndex(handler => handler[0] === 'links');
    
    if (linksHandlerIndex !== -1) {
      const handler = [...dataset[linksHandlerIndex]];
      const links = [...handler[1]];
      const linkCopy = { ...links[linkIndex] };
      
      let types = Array.isArray(linkCopy.type) ? [...linkCopy.type] : [linkCopy.type];
      types.splice(typeIndex, 1);
      if (types.length === 0) types = ['link-type'];
      linkCopy.type = types.length === 1 ? types[0] : types;
      
      links[linkIndex] = linkCopy;
      handler[1] = links;
      dataset[linksHandlerIndex] = handler;
      updateWorkingQuestion('dataset', dataset);
    }
  }
  
  function updateLinkProperty(property, value) {
    const dataset = [...question.dataset];
    const linksHandlerIndex = dataset.findIndex(handler => handler[0] === 'links');
    
    if (linksHandlerIndex !== -1) {
      const handler = [...dataset[linksHandlerIndex]];
      const links = [...handler[1]];
      const linkCopy = { ...links[linkIndex] };
      linkCopy[property] = value;
      
      links[linkIndex] = linkCopy;
      handler[1] = links;
      dataset[linksHandlerIndex] = handler;
      updateWorkingQuestion('dataset', dataset);
    }
  }
  
  function getLinkCustomProperties() {
    const reservedKeys = ['source', 'target', 'type'];
    return Object.entries(link)
      .filter(([key]) => !reservedKeys.includes(key))
      .map(([key, value]) => ({ key, value: String(value) }));
  }
  
  function updateCustomProperties(newProperties) {
    const dataset = [...question.dataset];
    const linksHandlerIndex = dataset.findIndex(handler => handler[0] === 'links');
    
    if (linksHandlerIndex !== -1) {
      const handler = [...dataset[linksHandlerIndex]];
      const links = [...handler[1]];
      
      // Start with basic link properties
      const linkCopy = {
        source: link.source,
        target: link.target,
        type: link.type
      };
      
      // Add custom properties
      newProperties.forEach(prop => {
        if (prop.key && prop.key.trim()) {
          linkCopy[prop.key] = prop.value;
        }
      });
      
      links[linkIndex] = linkCopy;
      handler[1] = links;
      dataset[linksHandlerIndex] = handler;
      updateWorkingQuestion('dataset', dataset);
    }
  }
  
  function getAvailableQuestionReferences() {
    const currentQuestionIndex = parseInt(question.id?.split('--')[1]) || questionIndex || 0;
    const references = [];
    
    // Add previous questions
    for (let i = 0; i < currentQuestionIndex; i++) {
      references.push({
        group: 'Previous Questions',
        options: [
          { value: `__answer${i}__`, label: `Question ${i} (Single Answer)` },
          { value: `__answers${i}__`, label: `Question ${i} (All Answers)` },
          { value: `__manyAnswer${i}__`, label: `Question ${i} (Many Single)` },
          { value: `__manyAnswers${i}__`, label: `Question ${i} (Many All)` }
        ]
      });
    }
    
    return references;
  }
  
  function getSourceTargetOptions() {
    const baseOptions = [
      { group: 'Core References', options: [
        { value: '__self__', label: 'Self Node' },
        { value: '__answers__', label: 'This Question (All Answers)' },
        { value: '__answer__', label: 'This Question (Single Answer)' },
        { value: '__priorAnswer__', label: 'Previous Question (Single)' },
        { value: '__priorAnswers__', label: 'Previous Question (All)' }
      ]}
    ];
    
    const questionRefs = getAvailableQuestionReferences();
    
    return [...baseOptions, ...questionRefs];
  }
  

  $: isExpanded = selectedLinkIndex === linkIndex;
  $: link, linkTypes = getLinkTypeValues();
  $: link, customProperties = getLinkCustomProperties();
  $: question, sourceTargetOptions = getSourceTargetOptions();
</script>

<div class="link-config-item" class:active={isExpanded}>
  <div class="link-config-header" on:click={selectLink} role="button" tabindex="0">
    <div class="link-config-info">
      <strong>Link {linkIndex + 1}</strong>
      <span class="link-config-summary">
        {link.source || '__self__'} → {link.target || '__answers__'}
        {#if link.type}
          ({Array.isArray(link.type) ? link.type.join(', ') : link.type})
        {/if}
      </span>
    </div>
    <div class="link-config-actions" on:click|stopPropagation>
      <button 
        class="btn btn-small btn-secondary"
        on:click={() => dispatch('duplicate')}
        title="Duplicate link"
      >
        📋
      </button>
      <button 
        class="btn btn-small btn-danger"
        on:click={() => dispatch('remove')}
        title="Remove link"
      >
        ×
      </button>
    </div>
  </div>
  
  {#if isExpanded}
    <div class="link-config-details">
      
      <!-- Source and Target Configuration -->
      <div class="link-detail-section">
        <label>Link Connection</label>
        <div class="source-target-editor">
          <div class="source-target-item">
            <label class="source-target-label">Source:</label>
            <select 
              value={link.source || '__self__'}
              on:change={(e) => updateLinkProperty('source', e.target.value)}
              class="source-target-select"
            >
              {#each sourceTargetOptions as group}
                <optgroup label={group.group}>
                  {#each group.options as option}
                    <option value={option.value}>{option.label}</option>
                  {/each}
                </optgroup>
              {/each}
            </select>
          </div>

          <div class="arrow-indicator">→</div>

          <div class="source-target-item">
            <label class="source-target-label">Target:</label>
            <select 
              value={link.target || '__answers__'}
              on:change={(e) => updateLinkProperty('target', e.target.value)}
              class="source-target-select"
            >
              {#each sourceTargetOptions as group}
                <optgroup label={group.group}>
                  {#each group.options as option}
                    <option value={option.value}>{option.label}</option>
                  {/each}
                </optgroup>
              {/each}
            </select>
          </div>
        </div>
      
      </div>
      
      <!-- Link Type Values -->
      <div class="link-detail-section">
        <label>Link Type Values</label>
        <div class="type-values-editor">
          {#each linkTypes as typeValue, idx}
            <div class="type-item">
              <input 
                type="text"
                value={typeValue}
                on:input={(e) => updateLinkTypeValue(idx, e.target.value)}
                placeholder="e.g., collaborates, manages, works-with"
                class="type-field"
              />
              {#if linkTypes.length > 1}
                <button 
                  class="btn btn-small btn-danger"
                  on:click={() => removeLinkTypeValue(idx)}
                >
                  ×
                </button>
              {/if}
            </div>
          {/each}
          <button class="btn btn-secondary btn-small" on:click={addLinkTypeValue}>
            Add Type Value
          </button>
        </div>
      </div>

      <div class="link-detail-section">
        <PropertyEditor 
          properties={customProperties}
          reservedKeys={['source', 'target', 'type']}
          keyPlaceholder="Property name"
          valuePlaceholder="Property value"
          title="Custom Link Properties"
          description="Add custom properties to this link type (e.g., strength, category, weight)"
          on:update={(e) => updateCustomProperties(e.detail)}
        />
      </div>
      
    </div>
  {/if}
</div>

<style lang="scss">
  .link-config-item {
    border: 2px solid #e5e7eb;
    border-radius: 8px;
    overflow: hidden;
    transition: all 0.2s ease;
    background: #fff;
    
    &.active {
      border-color: #3b82f6;
      box-shadow: 0 0 0 1px #3b82f6;
    }
  }
  
  .link-config-header {
    background: #f9fafb;
    padding: 12px;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    transition: background-color 0.2s ease;
    
    &:hover {
      background: #f3f4f6;
    }
    
    &:focus {
      outline: 2px solid #3b82f6;
      outline-offset: -2px;
    }
  }
  
  .link-config-info {
    flex: 1;
    
    strong {
      display: block;
      margin-bottom: 4px;
      color: #374151;
      font-size: 0.8rem;
    }
    
    .link-config-summary {
      font-size: 0.7rem;
      color: #6b7280;
      font-family: monospace;
      background: rgba(59, 130, 246, 0.1);
      padding: 2px 6px;
      border-radius: 3px;
    }
  }
  
  .link-config-actions {
    display: flex;
    gap: 6px;
  }
  
  .link-config-details {
    padding: 16px;
    background: #fff;
    border-top: 1px solid #e5e7eb;
  }
  
  .link-detail-section {
    margin-bottom: 20px;
    
    &:last-child {
      margin-bottom: 0;
    }
    
    > label {
      display: block;
      font-weight: 500;
      margin-bottom: 8px;
      color: #374151;
      font-size: 0.8rem;
    }
  }
  
  .source-target-editor {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    gap: 12px;
    align-items: center;
    background: #f9fafb;
    padding: 12px;
    border-radius: 6px;
    border: 1px solid #e5e7eb;
  }
  
  .source-target-item {
    display: flex;
    flex-direction: column;
    gap: 6px;
    
    .source-target-label {
      font-weight: 500;
      color: #374151;
      font-size: 0.75rem;
    }
    
    .source-target-select {
      background: #fff;
      border: 1px solid #d1d5db;
      padding: 6px 8px;
      border-radius: 4px;
      font-size: 0.7rem;
      font-family: monospace;
      
      &:focus {
        outline: none;
        border-color: #3b82f6;
        box-shadow: 0 0 0 1px #3b82f6;
      }
    }
  }
  
  .arrow-indicator {
    font-size: 1.2rem;
    color: #6b7280;
    font-weight: bold;
    text-align: center;
  }
  
  .link-requirement-note {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
    padding: 8px;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 4px;
    
    .requirement-icon {
      font-size: 0.8rem;
      flex-shrink: 0;
    }
    
    .requirement-text {
      font-size: 0.7rem;
      color: #1e40af;
      
      code {
        background: #dbeafe;
        padding: 1px 4px;
        border-radius: 2px;
        font-family: monospace;
      }
    }
  }
  
  .type-values-editor {
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    padding: 10px;
    background: #f9fafb;
    
    .type-item {
      display: flex;
      gap: 8px;
      align-items: center;
      margin-bottom: 8px;
      
      &:last-child {
        margin-bottom: 0;
      }
      
      .type-field {
        flex: 1;
        padding: 6px 10px;
        border: 1px solid #d1d5db;
        border-radius: 4px;
        background: #fff;
        font-size: 0.75rem;
        
        &:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 1px #3b82f6;
        }
      }
    }
    
    .btn {
      margin-top: 8px;
    }
  }
  
  .btn {
    padding: 4px 8px;
    border: none;
    border-radius: 3px;
    cursor: pointer;
    font-size: 11px;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    transition: all 0.2s ease;
    
    &.btn-secondary {
      background: #10b981;
      color: white;
      
      &:hover {
        background: #059669;
      }
    }
    
    &.btn-danger {
      background: #ef4444;
      color: white;
      
      &:hover {
        background: #dc2626;
      }
    }
    
    &.btn-small {
      padding: 3px 6px;
      font-size: 10px;
    }
  }
</style>